'use client';
import {
  createAppManifest,
  guardDocumentTitle,
  setFavicon,
  setOverscrollBehavior,
  setThemeColor,
  updateViewport,
} from '@/lib/utils/browser';
import { useAuthBoot } from './login/context/AuthBootContext';
import { useContext, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { handleUrlParams } from '@/lib/utils/urlParams';
import { languageService } from '@/lib/services/language';
import coralogixRumService from '@/lib/services/coralogixRum';
import analyticsService from '@/lib/services/analytics';
import { sessionManager } from '@/lib/session';
import { initializeDomain } from '@/lib/utils/initializer';
import { httpClient } from '@/lib/http-client';
import { LocalizationContext } from '@/providers/LanguageProvider';
import { getEnvVariable } from '@/lib/utils/env';
import { logAppInfo } from '@/lib/utils/app';
import { webStorageUtil } from '@/lib/storage/webStorageUtil';
import { ELocalStorageKeys } from '@/lib/storage/enums';
import { initFacebookSdkForLoginCallback } from '@/lib/utils/facebookSdk';
import { scheduleAfterMainContentPaint } from '@/lib/utils/deferred';
import { runAfterCoverDismissed } from '@/app/shop/components/runAfterCoverDismissed';
import { isLoginPathname, isShopPathname } from '@/lib/utils/loginPath';
import { SESSION_STORAGE_KEYS } from '@/lib/consts/sessionStorage';
import { getStoreDomain } from '@/lib/utils/domain-client';
import { STORE_BOOTED_FLAG, announceLandingBoot } from '@/lib/landingBeacon';
import { captureLandingUrl } from '@/lib/utils/landingUrl';

const PUBLISHER_DOMAIN_HEADER = 'x-publisher-domain';

const LOGIN_RUM_FALLBACK_MS = 3_000;
const DEFAULT_RUM_FALLBACK_MS = 1_500;
const SHOP_RUM_CAP_MS = 20_000;

export const AppInitializer = () => {
  const { authBoot } = useAuthBoot();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchKey = searchParams.toString();
  const localizationState = useContext(LocalizationContext);
  const languageConfig = {
    publisherId: authBoot.storeTheme.publisherId,
    initialLanguage: authBoot.language,
    baseURL: getEnvVariable('NEXT_PUBLIC_API_BASE_URL'),
  };

  // Read the bearer from localStorage only — never accept it via React props
  // so the HttpOnly auth cookie stops being mirrored into the RSC payload /
  // HTML stream. `handleSuccessfulLogin` keeps localStorage in sync with the
  // cookie, so the client always has access when it should.
  // Skip when session_expired purge marked the token rejected so a stale LS
  // value cannot re-arm Authorization mid-login.
  let sessionRejected = false;
  if (typeof window !== 'undefined') {
    try {
      sessionRejected = sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_REJECTED) === '1';
    } catch {
      sessionRejected = false;
    }
  }
  const resolvedSession =
    typeof window !== 'undefined' && !sessionRejected
      ? (webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN) ?? undefined)
      : undefined;

  if (resolvedSession) {
    httpClient.setDefaultHeader('Authorization', `Bearer ${resolvedSession}`);
  } else if (typeof window !== 'undefined' && sessionRejected) {
    httpClient.removeDefaultHeader('Authorization');
  }

  // Persist publisher meta early so post-login webhooks can include publisherId
  // (staging returns 400 "Publisher not found" without it).
  if (typeof window !== 'undefined' && authBoot) {
    webStorageUtil.set(ELocalStorageKeys.PUBLISHER_META, authBoot);
  }

  // Events APIs resolve publisher by domain. On localhost use
  // NEXT_PUBLIC_STORE_DOMAIN — never window.location.hostname.
  if (typeof window !== 'undefined') {
    const storeDomain = getStoreDomain();
    if (storeDomain) {
      httpClient.setDefaultHeader(PUBLISHER_DOMAIN_HEADER, storeDomain);
    }
  }

  const initClientServices = () => {
    initializeDomain();
    sessionManager.initialize();
    // Send the browser session id (the same id webstore analytics events carry)
    // on every API call, so the gateway can forward it downstream and checkout
    // events (e.g. order.created) correlate with webstore events instead of a
    // generated acco-* id.
    httpClient.setDynamicHeader('x-session-id', () => sessionManager.getOrCreateSessionId() || null);
    languageService.initialize(languageConfig, localizationState);
  };

  useEffect(() => {
    // Tells the in-document beacon our code got this far, which is what separates
    // a landing that never ran anything from one that ran and still went nowhere.
    // First, so it is recorded even if something below throws.
    announceLandingBoot(STORE_BOOTED_FLAG);
    captureLandingUrl();
    initClientServices();
    // Mixpanel must be ready before shop-land / offers events — same as
    // store-frontend. Do not defer behind the first paint.
    analyticsService.initialize();
    const disconnectTitleGuard = guardDocumentTitle(authBoot);
    updateViewport(authBoot);
    setFavicon(authBoot);
    setThemeColor(authBoot);
    setOverscrollBehavior(authBoot);
    createAppManifest(authBoot);
    handleUrlParams();
    logAppInfo();
    return () => {
      disconnectTitleGuard();
    };
  }, []);

  useEffect(() => {
    captureLandingUrl();
  }, [pathname, searchKey]);

  // Coralogix RUM stays off the critical path.
  useEffect(() => {
    const initRum = () => {
      coralogixRumService.initialize(authBoot);
    };
    return isShopPathname(pathname)
      ? runAfterCoverDismissed(initRum, {
          delayAfterDismissMs: 0,
          capMs: SHOP_RUM_CAP_MS,
        })
      : scheduleAfterMainContentPaint(initRum, {
          fallbackMs: isLoginPathname(pathname)
            ? LOGIN_RUM_FALLBACK_MS
            : DEFAULT_RUM_FALLBACK_MS,
        });
  }, [pathname]);

  useEffect(() => {
    initFacebookSdkForLoginCallback(
      authBoot.integration.playersAuthentication.fbAppId,
    );
  }, [pathname, authBoot.integration.playersAuthentication.fbAppId]);

  return null;
};
